import React, { useRef, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Card, Spinner, Button } from 'react-bootstrap';
import './SessionGroupCard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faCalendarPlus, faCalendarAlt, faArrowDown, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { SparkleButton } from '../../components/SparkleEffect';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import {
  SmsConsentCheckbox,
  buildSmsConsentPayload,
} from '../../components/SmsConsent';

// Helper to get user's local time zone
const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Format date for ICS (Apple/Outlook) in local time with TZID
const formatICSDate = (date) => {
  // Format: YYYYMMDDTHHmmss
  const pad = (num) => (num < 10 ? '0' : '') + num;
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

// Generate ICS file for a group (multi-session)
const generateGroupICalFile = (sessions, group) => {
  if (!sessions || sessions.length === 0) return '#';
  try {
    const timeZone = getUserTimeZone();
    const events = sessions.map((session, idx) => {
      if (!session.scheduledAt) return '';
      const startDate = new Date(session.scheduledAt);
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);
      
      // PROMINENT JOIN URL EVERYWHERE!
      const joinUrlText = session.joinUrl ? `🔗 VIDEO LINK: ${session.joinUrl}` : '';
      
      let descriptionText = `🎯 WORKSHOP SESSION ${idx + 1}`;
      if (session.joinUrl) {
        descriptionText += `\n\n🚀 JOIN THE SESSION:\n${session.joinUrl}\n\n🔗 VIDEO LINK:\n${session.joinUrl}\n\n📱 CLICK TO JOIN:\n${session.joinUrl}`;
      }
      if (session.description || group?.description) {
        descriptionText += `\n\n📋 SESSION DETAILS:\n${session.description || group?.description}`;
      }
      if (session.joinUrl) {
        descriptionText += `\n\n⚡ QUICK ACCESS:\n${session.joinUrl}`;
      }
      
      const locationText = session.joinUrl
        ? `🔗 Video Link: ${session.joinUrl}`
        : 'Online Session (Link TBA)';
      
      return [
        'BEGIN:VEVENT',
        `SUMMARY:${session.title || `Session ${idx + 1}`}${session.joinUrl ? ` - ${session.joinUrl}` : ''}`,
        `DTSTART;TZID=${timeZone}:${formatICSDate(startDate)}`,
        `DTEND;TZID=${timeZone}:${formatICSDate(endDate)}`,
        `DESCRIPTION:${descriptionText}`,
        `LOCATION:${locationText}`,
        `URL:${session.joinUrl || ''}`,
        `UID:${session.id || `${group?.id || 'group'}-${idx}`}@lumiwealth.com`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        `DESCRIPTION:🚨 Workshop starting in 15 minutes! ${joinUrlText}`,
        'END:VALARM',
        'BEGIN:VALARM',
        'TRIGGER:-PT5M',
        'ACTION:DISPLAY',
        `DESCRIPTION:🔥 Session starts in 5 minutes! ${joinUrlText}`,
        'END:VALARM',
        'END:VEVENT'
      ].join('\r\n');
    }).join('\r\n');
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      `X-WR-TIMEZONE:${timeZone}`,
      'METHOD:PUBLISH',
      events,
      'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (e) {
    return '#';
  }
};

// Generate ICS file for a single session
const generateICalFile = (session) => {
  if (!session || !session.scheduledAt) return '#';
  try {
    const timeZone = getUserTimeZone();
    const startDate = new Date(session.scheduledAt);
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 1);
    
    // PROMINENT JOIN URL EVERYWHERE!
    const joinUrlText = session.joinUrl ? `🔗 VIDEO LINK: ${session.joinUrl}` : '';
    
    let descriptionText = `🎯 WORKSHOP SESSION`;
    if (session.joinUrl) {
      descriptionText += `\n\n🚀 JOIN THE SESSION:\n${session.joinUrl}\n\n🔗 ZOOM LINK:\n${session.joinUrl}\n\n📱 CLICK TO JOIN:\n${session.joinUrl}`;
    }
    if (session.description) {
      descriptionText += `\n\n📋 SESSION DETAILS:\n${session.description}`;
    }
    if (session.joinUrl) {
      descriptionText += `\n\n⚡ QUICK ACCESS:\n${session.joinUrl}`;
    }
    
    const locationText = session.joinUrl 
      ? `🔗 Zoom Meeting: ${session.joinUrl}` 
      : 'Online via Zoom (Link TBA)';
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      `X-WR-TIMEZONE:${timeZone}`,
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${session.title || 'Session'}${session.joinUrl ? ` - ${session.joinUrl}` : ''}`,
      `DTSTART;TZID=${timeZone}:${formatICSDate(startDate)}`,
      `DTEND;TZID=${timeZone}:${formatICSDate(endDate)}`,
      `DESCRIPTION:${descriptionText}`,
      `LOCATION:${locationText}`,
      `URL:${session.joinUrl || ''}`,
      `UID:${session.id || 'session'}@lumiwealth.com`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      `DESCRIPTION:🚨 Workshop starting in 15 minutes! ${joinUrlText}`,
      'END:VALARM',
      'BEGIN:VALARM',
      'TRIGGER:-PT5M',
      'ACTION:DISPLAY',
      `DESCRIPTION:🔥 Session starts in 5 minutes! ${joinUrlText}`,
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (e) {
    return '#';
  }
};

// Generate Google Calendar link for a single session (local time, includes Zoom link)
const generateGoogleCalLink = (session) => {
  if (!session || !session.scheduledAt) return '#';
  const startDate = new Date(session.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 1);
  const pad = (num) => (num < 10 ? '0' : '') + num;
  // Google Calendar expects local time with time zone
  const formatDate = (date) =>
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  
  // SUPER PROMINENT JOIN URL FOR GOOGLE CALENDAR
  let descriptionText = `🎯 WORKSHOP SESSION`;
  if (session.joinUrl) {
    descriptionText += `\n\n🚀 JOIN THE SESSION:\n${session.joinUrl}\n\n🔗 ZOOM LINK:\n${session.joinUrl}\n\n📱 CLICK TO JOIN:\n${session.joinUrl}`;
  }
  if (session.description) {
    descriptionText += `\n\n📋 SESSION DETAILS:\n${session.description}`;
  }
  if (session.joinUrl) {
    descriptionText += `\n\n⚡ QUICK ACCESS:\n${session.joinUrl}`;
  }
  
  const locationText = session.joinUrl 
    ? `🔗 Zoom Meeting: ${session.joinUrl}` 
    : 'Online via Zoom (Link TBA)';
  
  const sessionTitle = session.joinUrl 
    ? `${session.title || 'Session'} - ${session.joinUrl}`
    : session.title || 'Session';
  
  const timeZone = getUserTimeZone();
  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(sessionTitle)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(descriptionText)}&location=${encodeURIComponent(locationText)}&ctz=${encodeURIComponent(timeZone)}`;
  return googleCalUrl;
};

// Generate Google Calendar link for a group (first session, local time, includes Zoom link)
const generateGoogleCalMultiLink = (sessions, group) => {
  if (!sessions || sessions.length === 0) return '#';
  const first = sessions[0];
  if (!first.scheduledAt) return '#';
  const startDate = new Date(first.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 1);
  const pad = (num) => (num < 10 ? '0' : '') + num;
  const formatDate = (date) =>
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  
  // SUPER PROMINENT JOIN URL FOR GOOGLE CALENDAR
  let descriptionText = `🎯 WORKSHOP COHORT - FIRST SESSION`;
  if (first.joinUrl) {
    descriptionText += `\n\n🚀 JOIN THE SESSION:\n${first.joinUrl}\n\n🔗 VIDEO LINK:\n${first.joinUrl}\n\n📱 CLICK TO JOIN:\n${first.joinUrl}`;
  }
  if (group?.description) {
    descriptionText += `\n\n📋 COHORT DETAILS:\n${group.description}`;
  }
  if (first.description) {
    descriptionText += `\n\n📋 FIRST SESSION:\n${first.description}`;
  }
  if (first.joinUrl) {
    descriptionText += `\n\n⚡ QUICK ACCESS:\n${first.joinUrl}`;
  }
  
  const locationText = first.joinUrl
    ? `🔗 Video Link: ${first.joinUrl}`
    : 'Online Session (Link TBA)';
  
  const cohortTitle = first.joinUrl 
    ? `${group?.name || 'Workshop Cohort'} - ${first.joinUrl}`
    : group?.name || 'Workshop Cohort';
  
  const timeZone = getUserTimeZone();
  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(cohortTitle)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(descriptionText)}&location=${encodeURIComponent(locationText)}&ctz=${encodeURIComponent(timeZone)}`;
  return googleCalUrl;
}

// Utility to check if any session is currently active (within -60 to +15 min)
function hasActiveSession(sessions) {
  if (!sessions || !Array.isArray(sessions) || sessions.length === 0) return false;
  const now = new Date();
  return sessions.some(session => {
    if (!session.scheduledAt) return false;
    const sessionTime = new Date(session.scheduledAt);
    const diffMinutes = (sessionTime - now) / (1000 * 60);
    return diffMinutes >= -60 && diffMinutes <= 15;
  });
}

// Helper: get time window for join button (returns {show, session, countdownText})
function getJoinWindowSession(sessions) {
  if (!sessions || !Array.isArray(sessions) || sessions.length === 0) {
    return { show: false, session: null, countdownText: "" };
  }
  
  const now = new Date();
  
  for (const session of sessions) {
    if (!session.scheduledAt) {
      continue;
    }
    
    const start = new Date(session.scheduledAt);
    // Use durationMinutes if available, otherwise default to 60 minutes
    const durationMinutes = session.durationMinutes || 60;
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + durationMinutes);
    
    const beforeStart = new Date(start);
    beforeStart.setHours(beforeStart.getHours() - 1); // 1 hour before start
    
    if (now >= beforeStart && now <= end) {
      let countdownText = "";
      if (now < start) {
        const diffMs = start - now;
        const diffSec = Math.floor(diffMs / 1000);
        const hours = Math.floor(diffSec / 3600);
        const mins = Math.floor((diffSec % 3600) / 60);
        const secs = diffSec % 60;
        if (hours > 0) {
          countdownText = `The session starts in ${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""}`;
        } else if (mins > 0) {
          countdownText = `The session starts in ${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
        } else {
          countdownText = `The session starts in ${secs} second${secs !== 1 ? "s" : ""}`;
        }
      } else if (now >= start && now <= end) {
        countdownText = "The session is currently in progress!";
      } else {
        countdownText = "The session recently ended.";
      }
      
      return { show: true, session, countdownText, sessionStart: start };
    }
  }
  
  return { show: false, session: null, countdownText: "" };
}

// Helper: get time window for public course join button (same logic, just renamed for clarity)
function getPublicJoinWindowSession(sessions) {
  // For simplicity, use the same logic as paid courses - 1 hour before to session end
  return getJoinWindowSession(sessions);
}

function getYoutubeVideoId(url) {
  if (!url) return null;
  try {
    if (url.includes('youtube.com/live/')) {
      const match = url.match(/youtube\.com\/live\/([^\/\?&]+)/);
      if (match && match[1]) return match[1];
    }
    if (url.includes('youtube.com/watch')) {
      const urlObj = new URL(url);
      const v = urlObj.searchParams.get('v');
      if (v) return v;
    }
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^\/\?&]+)/);
      if (match && match[1]) return match[1];
    }
    if (url.includes('youtube.com/embed/')) {
      const match = url.match(/youtube\.com\/embed\/([^\/\?&]+)/);
      if (match && match[1]) return match[1];
    }
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) return match[2];
    return null;
  } catch {
    return null;
  }
}

function formatChapterTime(seconds) {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
}

const SessionGroupCard = ({
  group,
  isCurrent,
  isRegistered,
  isRegistering,
  isAuthenticated,
  user,
  apiError,
  formErrors,
  guestInfo,
  setGuestInfo,
  handleRegistration, // For current cohort
  handleUnregister,   // For current cohort
  handleUpcomingCohortRegistration, // For specific upcoming cohort
  handleUpcomingCohortUnregister,   // For specific upcoming cohort
  formatWorkshopDate,
  registrationConfirmationRef,
  courseUserHasAccess, 
  courseRequiresPro, 
  userHasProSubscription, 
  requiredAccessLabel = 'Pro plan',
  upgradePath = '/upgrade-to-pro',
  upgradeCta = 'Upgrade to Pro',
  courseIsPublic = false, // ADD: indicates if this is a public course (like WeeklyWorkshop)
  // handleShowPaywall, // REMOVE
  navigate, // ADD navigate prop
  loginWithRedirect, 
}) => {
  const sessions = group.sessions || [];
  const singleSession = sessions.length === 1 ? sessions[0] : null;
  const isMulti = !singleSession && sessions.length > 1;

  const registrationRef = useRef(null);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState('');

  const onRegisterClick = (extra = {}) => {
    if (isCurrent && typeof handleRegistration === 'function') {
      handleRegistration(extra);
    } else if (!isCurrent && typeof handleUpcomingCohortRegistration === 'function') {
      handleUpcomingCohortRegistration(group.id, extra);
    }
  };

  const onUnregisterClick = () => {
    if (isCurrent && typeof handleUnregister === 'function') {
      handleUnregister();
    } else if (!isCurrent && typeof handleUpcomingCohortUnregister === 'function') {
      handleUpcomingCohortUnregister(group.id);
    }
  };


  // Registration button with sparkle effect and working click handler
  const renderRegistrationButton = () => {
    // 1. Admin Lock (courseUserHasAccess === false)
    if (courseUserHasAccess === false) {
      return isAuthenticated ? (
        <Button variant="primary" disabled className="edu-register-button" style={{ opacity: 0.7, cursor: "not-allowed", width: "100%", maxWidth: 400 }}>
          <i className="fas fa-lock me-2"></i> You do not have access to register
        </Button>
      ) : ( // Not authenticated, and admin-locked
        courseRequiresPro ? ( // Admin-locked, !isAuthenticated, AND course requires Pro
          <SparkleButton
            className="edu-register-button"
            style={{ width: "100%", maxWidth: 400, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            onClick={() => {
              pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: group?.courseId || 'unknown_course', source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: group.id, reason: 'not_authenticated_pro_required_admin_locked_sgs' });
              navigate(upgradePath); // Direct navigation
            }}
          >
            <i className="fas fa-rocket me-2"></i>
            {upgradeCta} to Register
          </SparkleButton>
        ) : ( // Admin-locked, !isAuthenticated, AND course does NOT require Pro (free but locked)
          <SparkleButton className="edu-register-button" style={{ width: "100%", maxWidth: 400 }} onClick={() => loginWithRedirect({ appState: { returnTo: window.location.pathname }})}>
            <i className="fas fa-sign-in-alt me-2"></i>
            Login to Register
          </SparkleButton>
        )
      );
    }

    // 2. Course Requires Pro Subscription (implicitly courseUserHasAccess !== false here)
    if (courseRequiresPro) {
      if (!isAuthenticated) { // Not admin locked, requires Pro, AND !isAuthenticated
        return (
          <SparkleButton 
            className="edu-register-button" 
            style={{ width: "100%", maxWidth: 400, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} 
            onClick={() => {
              pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: group?.courseId || 'unknown_course', source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: group.id, reason: 'not_authenticated_pro_required_sgs' });
              navigate(upgradePath); // Direct navigation
            }}
          >
            <i className="fas fa-rocket me-2"></i>
            {upgradeCta} to Register
          </SparkleButton>
        );
      }
      if (!userHasProSubscription) { // Authenticated, requires Pro, but !hasProSubscription
        return (
          <>
            <p className="sgs-pro-pitch-text">
              Registering for this cohort requires {requiredAccessLabel}. You can still watch any recordings included with your current access.
            </p>
            <SparkleButton 
              className="edu-register-button" 
              style={{ width: "100%", maxWidth: 400, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} 
              onClick={() => {
                pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: group?.courseId || 'unknown_course', source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: group.id, reason: 'pro_required_sgs' });
                navigate(upgradePath);
              }}
            >
              {upgradeCta} to Register
            </SparkleButton>
          </>
        );
      }
      // Authenticated AND has pro plan - proceed to normal registration button
    }

    // 3. Standard Registration (Free course OR Pro course with Pro access)
    if (!isAuthenticated && !courseRequiresPro) { // Guest registration for free course
        // Render the guest registration form directly (no login required)
        return renderGuestForm(onRegisterClick);
    }
    
    // Authenticated (either for free course, or for pro course with pro access)
    // Or guest form for free course if that logic is re-enabled
    if (isAuthenticated) {
        return isRegistering ? (
            <Button variant="primary" disabled className="edu-register-button" style={{ width: "100%", maxWidth: 400 }}>
                <Spinner animation="border" size="sm" className="me-2" />
                {isMulti ? "Registering for all sessions..." : "Registering..."}
            </Button>
        ) : (
            <SparkleButton className="edu-register-button" style={{ width: "100%", maxWidth: 400 }} onClick={onRegisterClick}>
                {isMulti ? "Register for all sessions" : "Register"}
            </SparkleButton>
        );
    }

    // Fallback for guest registration if enabled for free courses (currently points to login)
    // This assumes guestInfo, setGuestInfo, formErrors, apiError are available if guest form is used.
    // If guest registration is NOT a path for any course type, this part can be simplified/removed.
    if (!isAuthenticated && !courseRequiresPro) { // Logic for guest form for FREE courses
        return renderGuestForm(onRegisterClick);
    }
    
    // Default fallback (should ideally not be reached if logic above is complete)
    return null;
  };
  

  // Guest registration form (only relevant if courseDoesNotRequirePro and not authenticated)
  const renderGuestForm = (onSubmit) => {
    const handleGuestSubmit = () => {
      onSubmit({
        smsConsent: buildSmsConsentPayload({
          source: 'course_guest_registration_form',
          phoneNumber: guestInfo.phone,
          accepted: smsConsentAccepted,
        }),
      });
    };

    return (
      <div className="edu-guest-registration">
      <h4 className="edu-registration-instruction">Enter your information below to register</h4>
      {apiError && (
        <div className="edu-form-error-message">
          <i className="fas fa-exclamation-circle"></i>
          {apiError}
        </div>
      )}
      <div className="edu-guest-form">
        <div className="edu-form-group">
          <input
            type="text"
            placeholder="Your full name"
            className={`edu-guest-input ${formErrors.name ? 'has-error' : ''}`}
            style={{ color: "var(--education-ink, #fff)" }}
            value={guestInfo.name}
            onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
            required
          />
          {formErrors.name && (
            <div className="edu-field-error-message">
              <i className="fas fa-exclamation-circle"></i> {formErrors.name}
            </div>
          )}
        </div>
        <div className="edu-form-group">
          <input
            type="email"
            placeholder="Your email address"
            className={`edu-guest-input ${formErrors.email ? 'has-error' : ''}`}
            style={{ color: "var(--education-ink, #fff)" }}
            value={guestInfo.email}
            onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
            required
          />
          {formErrors.email && (
            <div className="edu-field-error-message">
              <i className="fas fa-exclamation-circle"></i> {formErrors.email}
            </div>
          )}
        </div>
        <div className="edu-form-group">
          <input
            type="tel"
            placeholder="Your phone number"
            className={`edu-guest-input ${formErrors.phone ? 'has-error' : ''}`}
            style={{ color: "var(--education-ink, #fff)" }}
            value={guestInfo.phone}
            onChange={(e) => {
              setGuestInfo({...guestInfo, phone: e.target.value});
              if (smsConsentAccepted) setSmsConsentAccepted(false);
              if (smsConsentError) setSmsConsentError('');
            }}
          />
          {formErrors.phone && (
            <div className="edu-field-error-message">
              <i className="fas fa-exclamation-circle"></i> {formErrors.phone}
            </div>
          )}
        </div>
        <SmsConsentCheckbox
          id={`course-${group.id || 'guest'}-sms-consent`}
          checked={smsConsentAccepted}
          onChange={(checked) => {
            setSmsConsentAccepted(checked);
            if (checked) setSmsConsentError('');
          }}
          disabled={!guestInfo.phone.trim()}
          error={smsConsentError}
        />
        {/* Original button call, ensure it uses the passed onSubmit */}
        {isRegistering ? (
             <Button variant="primary" disabled className="edu-register-button w-100">
                <Spinner animation="border" size="sm" className="me-2" /> Registering...
             </Button>
         ) : (
            <SparkleButton className="edu-register-button w-100" onClick={handleGuestSubmit}>
                Register Now
            </SparkleButton>
        )}
      </div>
    </div>
    );
  };

  // --- Join Now logic ---
  const [joinCountdown, setJoinCountdown] = useState("");
  // Use appropriate join window function based on course type
  const joinWindow = courseIsPublic ? getPublicJoinWindowSession(sessions) : getJoinWindowSession(sessions);

  // Live countdown update (every second)
  useEffect(() => {
    if (!joinWindow.show || !joinWindow.session) return;
    let interval;
    function updateCountdown() {
      const now = new Date();
      const start = new Date(joinWindow.session.scheduledAt);
      const end = new Date(start);
      end.setHours(end.getHours() + 1);
      const afterEnd = new Date(end);
      afterEnd.setHours(afterEnd.getHours() + 3);
      let countdownText = "";
      if (now < start) {
        const diffMs = start - now;
        const diffSec = Math.floor(diffMs / 1000);
        const hours = Math.floor(diffSec / 3600);
        const mins = Math.floor((diffSec % 3600) / 60);
        const secs = diffSec % 60;
        if (hours > 0) {
          countdownText = `The session starts in ${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""}`;
        } else if (mins > 0) {
          countdownText = `The session starts in ${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
        } else {
          countdownText = `The session starts in ${secs} second${secs !== 1 ? "s" : ""}`;
        }
      } else if (now >= start && now <= end) {
        countdownText = "The session is currently in progress!";
      } else if (now > end && now <= afterEnd) {
        countdownText = "The session recently ended.";
      } else {
        countdownText = "";
      }
      setJoinCountdown(countdownText);
    }
    updateCountdown();
    interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [joinWindow.show, joinWindow.session]);

  // --- Join Now area ---
  const renderJoinNowArea = () => {
    // For public courses, show join button regardless of registration status
    // For paid courses, only show if registered
    const shouldShowJoinButton = courseIsPublic 
      ? (joinWindow.show && joinWindow.session && joinWindow.session.joinUrl)
      : (isRegistered && joinWindow.show && joinWindow.session && joinWindow.session.joinUrl);
    
    // If in join window but no URL, show a message
    if (joinWindow.show && joinWindow.session && !joinWindow.session.joinUrl) {
      return (
        <div className="join-now-area" style={{ background: 'linear-gradient(135deg, #666, #444)' }}>
          <div className="session-countdown">{joinCountdown}</div>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
            {joinWindow.session.title || 'Workshop Session'}
          </p>
          <p style={{ color: '#ffa500', marginBottom: 0 }}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Join link will be available shortly
          </p>
        </div>
      );
    }
      
    if (!shouldShowJoinButton) {
      return null;
    }
    
    const session = joinWindow.session;
    const sessionTime = formatWorkshopDate(session.scheduledAt);
    const handleJoinClick = () => {
      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_JOIN_SESSION,
        courseType: group?.courseType || 'workshop',
        groupId: group.id,
        sessionId: session.id,
        isAuthenticated: !!user,
        email: user?.email
      });
      window.open(session.joinUrl, '_blank', 'noopener,noreferrer');
    };
    
    // For public courses, always just say "Join Now"
    // For paid courses, differentiate between registered users and guests
    const joinButtonText = courseIsPublic 
      ? "Join Now"
      : (isRegistered ? "Join Now" : "Join as Guest");
      
    return (
      <div className="join-now-area">
        <div className="session-countdown">{joinCountdown}</div>
        <p className="mb-3" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
          {session.title} • {sessionTime}
        </p>
        <SparkleButton 
          className="join-button-large"
          style={{ minWidth: '200px' }}
          onClick={handleJoinClick}
        >
          <i className="fas fa-video me-2"></i>
          {joinButtonText}
        </SparkleButton>
      </div>
    );
  };

  // Add to calendar area (hide if join window is active)
  const renderUnifiedCalendarArea = () => {
    if (!isRegistered || (joinWindow.show && joinWindow.session && joinWindow.session.joinUrl)) return null;
    if (!isRegistered) return null;
    const isMulti = !singleSession && sessions.length > 1;
    const icsUrl = isMulti
      ? generateGroupICalFile(sessions, group)
      : generateICalFile(singleSession);

    // Handler for Google Calendar button
    const handleGoogleCalClick = (e) => {
      e.preventDefault();
      // --- Analytics: Add to Google Calendar ---
      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_ADD_TO_CALENDAR,
        courseType: group?.courseType || 'workshop', // fallback
        groupId: group.id,
        calendarType: 'google',
        isAuthenticated: !!user,
        email: user?.email
      });
      if (isMulti) {
        sessions.forEach(session => {
          if (session && session.scheduledAt) {
            const url = generateGoogleCalLink(session);
            if (url && url !== '#') {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }
        });
      } else if (singleSession && singleSession.scheduledAt) {
        const url = generateGoogleCalLink(singleSession);
        if (url && url !== '#') {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      }
    };

    return (
      <div className="edu-calendar-area" style={{ 
        background: "linear-gradient(135deg, var(--education-calendar-start, #667eea), var(--education-calendar-end, #764ba2))",
        borderRadius: "12px",
        padding: "1.5rem",
        marginTop: "1.5rem",
        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.25)"
      }}>
        <div className="edu-calendar-area-title" style={{ 
          color: "white", 
          fontSize: "1.3rem", 
          marginBottom: "1rem",
          fontWeight: "600",
          textAlign: "center"
        }}>
          📅 Add Sessions to Your Calendar
        </div>
        <p style={{ 
          color: "rgba(255, 255, 255, 0.9)", 
          textAlign: "center", 
          marginBottom: "1.5rem",
          fontSize: "1rem"
        }}>
          Don't miss your sessions! Add them to your calendar with video links included.
        </p>
        <div className="edu-calendar-area-buttons" style={{ 
          display: "flex", 
          gap: "1rem", 
          flexDirection: "column"
        }}>
          <a
            href={icsUrl}
            download={
              isMulti
                ? `${group.name ? group.name.replace(/\s+/g, '_').toLowerCase() : 'workshop'}-all-sessions.ics`
                : `workshop-${singleSession?.title || 'session'}.ics`
            }
            className="edu-calendar-area-btn"
            style={{
              background: "var(--education-register-start, #FF8C00)",
              color: "white",
              padding: "0.9rem 1.5rem",
              borderRadius: "8px",
              textDecoration: "none",
              fontSize: "1rem",
              fontWeight: "500",
              textAlign: "center",
              transition: "all 0.3s ease",
              border: "1px solid #FF7F00"
            }}
            onClick={() => {
              // --- Analytics: Add to Apple/Outlook Calendar ---
              pushEvent({
                event: ANALYTICS_EVENTS.COURSE_ADD_TO_CALENDAR,
                courseType: group?.courseType || 'workshop',
                groupId: group.id,
                calendarType: 'apple_outlook',
                isAuthenticated: !!user,
                email: user?.email
              });
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--education-register-end, #FF7F00)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--education-register-start, #FF8C00)";
            }}
          >
            {isMulti
              ? "📱 Add All Sessions to Apple/Outlook"
              : "📱 Add to Apple/Outlook Calendar"}
          </a>
          <a
            href="#"
            onClick={handleGoogleCalClick}
            className="edu-calendar-area-btn"
            style={{
              background: "var(--education-calendar-google, #4285F4)",
              color: "white",
              padding: "0.9rem 1.5rem",
              borderRadius: "8px",
              textDecoration: "none",
              fontSize: "1rem",
              fontWeight: "500",
              textAlign: "center",
              transition: "all 0.3s ease",
              border: "1px solid #3367d6"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--education-calendar-google-hover, #3367d6)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--education-calendar-google, #4285F4)";
            }}
          >
            {isMulti ? "🗓️ Add All Sessions to Google Calendar" : "🗓️ Add to Google Calendar"}
          </a>
        </div>
        {isMulti && (
          <div style={{ 
            fontSize: "0.9rem", 
            color: "rgba(255, 255, 255, 0.8)", 
            marginTop: "1rem",
            textAlign: "center",
            background: "rgba(255, 255, 255, 0.1)",
            padding: "0.8rem",
            borderRadius: "6px"
          }}>
            <span>
              <strong>💡 Tip:</strong> Google Calendar will open a separate tab for each session with Zoom links included.
            </span>
          </div>
        )}
      </div>
    );
  };

  // Unregister button (always below calendar area, only when registered and authenticated)
  const renderUnregisterButton = () => (
    <div className="edu-unregister-container" style={{ marginTop: 18, textAlign: "center" }}>
      <Button
        variant="link"
        className="edu-unregister-link"
        style={{
          color: "var(--education-muted, #bbbbbb)",
          textDecoration: "underline",
          fontSize: "0.97rem",
          opacity: 0.7,
          padding: 0,
          margin: 0,
        }}
        onClick={isCurrent ? handleUnregister : () => handleUpcomingCohortUnregister(group.id)}
        tabIndex={0}
        aria-label="Unregister"
      >
        Unregister
      </Button>
    </div>
  );

  // Main rendering logic for registration area
  const renderRegistrationArea = () => {
    // For public courses, show join button if in join window (regardless of registration)
    // For paid courses, only show join button if registered AND in join window
    const shouldShowJoinArea = courseIsPublic 
      ? (joinWindow.show && joinWindow.session && joinWindow.session.joinUrl)
      : (isRegistered && joinWindow.show && joinWindow.session && joinWindow.session.joinUrl);
    
    if (shouldShowJoinArea) {
      return renderJoinNowArea();
    }
    
    if (isRegistered) {
      return (
        <div className="edu-registration-confirmed edu-success-box" ref={registrationConfirmationRef}>
          <div className="edu-confirmed-message edu-confirmed-message-responsive">
            <div className="edu-confirmed-icon">
              ✅
            </div>
            <div className="edu-confirmed-title">
              {singleSession ? "You're registered for this session!" : "You're registered for this cohort!"}
            </div>
            <p className="edu-confirmed-subtitle">
              Check your inbox for confirmation details!
            </p>
          </div>
          <div className="edu-registration-details" style={{
            background: "rgba(40, 167, 69, 0.1)",
            border: "1px solid rgba(40, 167, 69, 0.3)",
            borderRadius: "8px",
            padding: "1rem",
            marginBottom: "1rem",
            textAlign: "center"
          }}>
            <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem" }}>
              Confirmation sent to <strong>{isAuthenticated && user ? user.email : 
                localStorage.getItem(`edu-workshop-registered-${group.id}`) ? 
                JSON.parse(localStorage.getItem(`edu-workshop-registered-${group.id}`)).email : 
                "your email"}</strong>
            </p>
            <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--education-muted, #666)" }}>
              Please check your inbox (including spam/promotions folders)
            </p>
          </div>
          {renderUnifiedCalendarArea()}
          {isAuthenticated && renderUnregisterButton()}
        </div>
      );
    }
    
    // Not registered, show appropriate registration button or guest form
    return renderRegistrationButton(); 
  };

  return (
    <Card 
      className={`edu-workshop-card ${isCurrent ? 'edu-current-cohort' : 'edu-upcoming-cohort-card'}`}
      id={!isCurrent ? `cohort-${group.id}` : undefined}
    >
      <Card.Body>
        <div className="edu-cohort-layout-container">
          <div className="edu-cohort-header">
            <div className={`edu-workshop-upcoming-badge ${isCurrent ? 'active' : 'coming-soon'}`}>
              {isCurrent ? 'Up Next' : 'Coming Soon'}
            </div>
            {(!singleSession) && (
              <div className="edu-workshop-group-container">
                {group.name && (
                  <h2 className="edu-workshop-group-name">{group.name}</h2>
                )}
                {group.description && (
                  <div
                    className="edu-workshop-group-description"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(group.description) }}
                  />
                )}
                {group.longDescription && (
                  <div
                    className="edu-workshop-group-long-description"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(group.longDescription) }}
                  />
                )}
                {!group.name && !group.description && isCurrent && (
                  <p className="edu-workshop-group-description">
                    Join our weekly workshop series to learn how to build, backtest, and deploy your own trading strategies.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="edu-cohort-sessions">
            <div className="edu-sessions-list">
              {sessions.map((session, index) => (
                <div key={session.id} className={singleSession ? "edu-single-session-card" : "edu-multi-session-card"}>
                  {session.scheduledAt && (
                    <div className={singleSession ? "edu-single-session-date" : "edu-multi-session-date"}>
                      <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 8, color: "#ff8c00" }} />
                      {formatWorkshopDate(session.scheduledAt)}
                    </div>
                  )}
                  {session.title && (
                    <div className={singleSession ? "edu-single-session-title" : "edu-multi-session-title"}>{session.title}</div>
                  )}
                  {session.description && (
                    <div
                      className={singleSession ? "edu-single-session-description" : "edu-multi-session-description"}
                      style={{ marginTop: 12 }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(session.description) }}
                    />
                  )}
                  {session.longDescription && (
                    <div
                      className={singleSession ? "edu-single-session-long-description" : "edu-multi-session-long-description"}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(session.longDescription) }}
                    />
                  )}

                  {session.recordingUrl && (
                    <div className="edu-active-session-recording">
                      <div className="edu-active-session-recording-header">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginRight: 8, color: "#ff8c00" }} />
                        Recording
                      </div>
                      {session.recordingSummary && (
                        <p className="edu-recording-summary">{session.recordingSummary}</p>
                      )}
                      {getYoutubeVideoId(session.recordingUrl) ? (
                        <div className="edu-iframe-container" style={{ aspectRatio: '16/9' }}>
                          <iframe
                            width="100%"
                            height="315"
                            src={`https://www.youtube.com/embed/${getYoutubeVideoId(session.recordingUrl)}?autoplay=0`}
                            title={session.title || `Session ${index + 1} recording`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ) : (
                        <a
                          className="edu-external-recording-link"
                          href={session.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Watch Recording
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2" />
                        </a>
                      )}
                      {session.recordingChapters && session.recordingChapters.length > 0 && (
                        <div className="edu-recording-chapters">
                          <h4>Chapters</h4>
                          <div className="edu-recording-chapter-list">
                            {session.recordingChapters.map((chapter, chapterIndex) => (
                              <div className="edu-recording-chapter" key={`${session.id}-chapter-${chapterIndex}`}>
                                <span>{formatChapterTime(chapter.startSeconds)}</span>
                                <strong>{chapter.title}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  

                  
                  {/* Subtle clickable inline reminder for multi-session, not registered */}
                  {isMulti && !isRegistered && (
                    <div
                      className="edu-register-inline-hint"
                      onClick={() => {
                        if (registrationRef.current) {
                          registrationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      style={{
                        fontSize: "0.93rem",
                        color: "#ffb347",
                        marginTop: 6,
                        marginBottom: -2,
                        fontWeight: 500,
                        opacity: 0.92,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4em",
                        userSelect: "none"
                      }}
                    >
                      Register below to join all sessions in this cohort.
                      <span style={{ display: "flex", alignItems: "center", marginLeft: 4 }}>
                        <FontAwesomeIcon icon={faArrowDown} style={{ color: "#ffb347", fontSize: 16 }} />
                        <FontAwesomeIcon icon={faArrowDown} style={{ color: "#ffb347", fontSize: 16, marginLeft: -4 }} />
                      </span>
                    </div>
                  )}

                  {/* Course Materials - positioned below session content */}
                  {session.materials && session.materials.length > 0 && (courseIsPublic || isRegistered) && (
                    <div className="edu-session-materials" style={{ 
                      marginTop: "20px", 
                      padding: "16px", 
                      backgroundColor: "var(--education-surface, #1a1a1a)",
                      borderRadius: "12px", 
                      border: "1px solid var(--education-border, #333333)"
                    }}>
                      <h4 style={{ 
                        fontSize: "16px", 
                        fontWeight: "600", 
                        color: "var(--education-ink, #ffffff)",
                        margin: "0 0 12px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}>
                        📚 Course Materials
                      </h4>
                      <div className="edu-materials-list" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {session.materials.map((material, materialIndex) => {
                          const getIconForType = (type) => {
                            switch (type) {
                              case 'google_slides': return '📊';
                              case 'google_docs': return '📄';
                              case 'google_drive': return '📁';
                              case 'pdf': return '📕';
                              case 'youtube': return '🎥';
                              case 'external_link': return '🔗';
                              default: return '📎';
                            }
                          };
                          
                          return (
                            <a
                              key={materialIndex}
                              href={material.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "12px 16px",
                                backgroundColor: "var(--education-surface, #2a2a2a)",
                                borderRadius: "8px",
                                border: "1px solid var(--education-border, #404040)",
                                textDecoration: "none",
                                color: "var(--education-ink, #ffffff)",
                                transition: "all 0.2s ease",
                                fontSize: "14px"
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "var(--education-canvas, #333333)";
                                e.target.style.borderColor = "var(--education-border, #555555)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "var(--education-canvas, #2a2a2a)";
                                e.target.style.borderColor = "var(--education-border, #404040)";
                              }}
                            >
                              <span style={{ fontSize: "18px" }}>{getIconForType(material.type)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontWeight: "500", 
                                  color: "var(--education-ink, #ffffff)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginBottom: material.description ? "4px" : "0"
                                }}>
                                  {material.title}
                                </div>
                                {material.description && (
                                  <div style={{ 
                                    fontSize: "12px", 
                                    color: "var(--education-muted, #cccccc)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {material.description}
                                  </div>
                                )}
                              </div>
                              <FontAwesomeIcon 
                                icon={faArrowUpRightFromSquare} 
                                style={{ 
                                  color: "var(--education-muted, #888888)",
                                  fontSize: "14px" 
                                }} 
                              />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {sessions.length === 0 && (
            <div className="edu-no-sessions-alert">
              <p>No sessions are currently scheduled for this cohort. Check back soon!</p>
            </div>
          )}
          <div className="edu-registration-area" ref={registrationRef}>
            {renderRegistrationArea()}
          </div>
          {/* Floating register bar for multi-session, not registered */}
          {isMulti && !isRegistered && courseUserHasAccess !== false && ( // Also check admin lock
             // The content of the floating bar should also respect courseRequiresPro and userHasProSubscription
             // For simplicity, this might just be a call to renderRegistrationButton() or a simplified version
             <div className="edu-floating-register-bar">
                {renderRegistrationButton()} 
             </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default SessionGroupCard;
